import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { catchError, delay, from, map, of, switchMap } from 'rxjs';
import postalCodes from '../../../assets/postal-codes/postal-codes.json';

export const errorObjectBuilder = (message: string) => ({
  message,
});

/**
 * @param {string} country
 * @return {ValidatorFn}
 */
export function postalCodeValidator(country: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.length === 0) {
      return null;
    }
    const postalCodeRegStr = (postalCodes as any)[country];
    if (!postalCodeRegStr) {
      // let it be if non conformant country is informed
      return null;
    }
    const postalCodeRegExp = new RegExp(`^${postalCodeRegStr}$`);
    const validPostalCode = postalCodeRegExp.test(control.value);
    return validPostalCode ? null : errorObjectBuilder('invalid-postal-code');
  };
}

/**
 * @param {string} dialCode
 * @return {ValidatorFn}
 */
export function phoneNumberValidator(dialCode: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const phoneNumber: string = control.value;
    const formattedDialCode = dialCode.slice(1);
    const phoneNumberPattern =
      /^\+?(\d{1,3})?[ -]?\(?\d{1,4}\)?[ -]?\d{1,4}[ -]?\d{1,9}$/;

    return phoneNumber.startsWith(formattedDialCode) &&
      phoneNumberPattern.test(phoneNumber)
      ? null
      : errorObjectBuilder('invalid-phone-number');
  };
}

/**
 * @return {ValidatorFn}
 */
export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const urlPattern =
      /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:\d{2,5})?(\/\S*)*$/i;
    const errorObject = errorObjectBuilder('invalid-url');
    const givenUrl: string = control.value;
    if (givenUrl === '' || !givenUrl) {
      return null;
    } else if (urlPattern.test(givenUrl)) {
      return null;
    } else {
      return errorObject;
    }
  };
}

/**
 * Validator for point rescue codes
 * @param {Function} asyncFunction
 * @param {string} errorMessage translatable error message
 * @return {AsyncValidatorFn}
 */
export function asyncValidator(
  asyncFunction: (arg0: any) => Promise<boolean>,
  errorMessage: string
): AsyncValidatorFn {
  return (control: AbstractControl) =>
    of(control.value).pipe(
      delay(500),
      switchMap((value) =>
        from(asyncFunction(value)).pipe(
          map((ok) => (ok ? null : errorObjectBuilder(errorMessage))),
          catchError((err) => of(errorObjectBuilder(err)))
        )
      )
    );
}

/**
 * Searches a form array for its own value, erroring if a value is already present
 * @param {FormArray} formArray
 * @param {Function} getValue
 * @return {ValidatorFn}
 */
export function uniqueControlFormArrayValidator(
  formArray: FormArray,
  getValue?: (arg0: AbstractControl) => any
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    getValue = getValue || ((ctrl) => ctrl.value);
    let ok = true;
    let occurrences = 0;

    for (const c of formArray.controls) {
      if (getValue(c) === control.value) {
        if (++occurrences === 2) {
          ok = false;
          break;
        }
      }
    }
    return ok ? null : errorObjectBuilder('field-not-unique');
  };
}

/**
 * Makes sure some control is not larger than biggerControl
 * @param {AbstractControl} biggerControl
 * @param {string} errorMessage
 * @return {ValidatorFn}
 */
export function lessThanOrEqualToValidator(
  biggerControl: AbstractControl,
  errorMessage: string = 'field-not-smaller'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control?.value == null || biggerControl?.value == null) {
      return null;
    }
    const ok = control.value <= biggerControl.value;
    return ok ? null : errorObjectBuilder(errorMessage);
  };
}

export const passwordMinLength = 8;

/**
 * @param {string} specificField
 * @return {ValidatorFn}
 */
export function genericRegisterValidator(specificField?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const name: string = control.value;
    let hasSpecialChars = Boolean(name?.match(/['"&\\><=+@`{}]/));
    if (specificField === 'businessName') {
      hasSpecialChars = Boolean(name?.match(/['"\\><=+@`{}]/));
    } else if (specificField === 'email') {
      hasSpecialChars = Boolean(name?.match(/['"&\\><=`{}]/));
    }
    const error = ['invalid-name-error-message'];
    const businessNameError = ['invalid-business-name-error-message'];
    const emailError = ['invalid-field-email-error-message'];

    if (hasSpecialChars && !specificField) {
      return error;
    } else if (hasSpecialChars && specificField === 'businessName') {
      return businessNameError;
    } else if (hasSpecialChars && specificField === 'email') {
      return emailError;
    }
    return null;
  };
}

/**
 * Decides if a field contains a valid password
 * @return {ValidatorFn}
 */
export function passwordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const password: string = control.value;
    const minLengthOk = password?.length >= passwordMinLength;
    const errors: {
      'invalid-password-too-short'?: string;
      'invalid-password-no-numbers'?: string;
      'invalid-password-no-special-chars'?: string;
      'invalid-password-no-uppercase'?: string;
      'invalid-password-no-lowercase'?: string;
    } = {};
    if (!minLengthOk) {
      errors['invalid-password-too-short'] = 'invalid-password-too-short';
    }
    const hasNumbers = Boolean(password?.match(/[0-9]/));
    if (!hasNumbers) {
      errors['invalid-password-no-numbers'] = 'invalid-password-no-numbers';
    }
    /* eslint-disable no-useless-escape */
    /* REASON: everything becomes green after this line if useless escape
       is not present */
    const hasSpecialChars = Boolean(
      password?.match(/[\^$*.[ \]{}()?"!@#%&\/\\,><':;|_~`=+-]/)
    );
    /* eslint-disable no-useless-escape */
    if (!hasSpecialChars) {
      errors['invalid-password-no-special-chars'] =
        'invalid-password-no-special-chars';
    }
    const hasUpperCase = Boolean(password?.match(/[A-Z]/));
    if (!hasUpperCase) {
      errors['invalid-password-no-uppercase'] = 'invalid-password-no-uppercase';
    }
    const hasLowerCase = Boolean(password?.match(/[a-z]/));
    if (!hasLowerCase) {
      errors['invalid-password-no-lowercase'] = 'invalid-password-no-lowercase';
    }
    if (
      errors['invalid-password-too-short'] ||
      errors['invalid-password-no-numbers'] ||
      errors['invalid-password-no-special-chars'] ||
      errors['invalid-password-no-uppercase'] ||
      errors['invalid-password-no-lowercase']
    ) {
      return errors;
    }
    return null;
  };
}

/**
 * Decides if a field has some exact value
 * @param {any} original value that must be exactly equal to validate
 * @return {ValidatorFn}
 */
export function exactValidator(original: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value === original
      ? null
      : errorObjectBuilder('invalid-fields-do-not-match');
  };
}

/**
 * Invalidates field if its value is already present in @list
 * @param {T[]} list
 * @param {function(el: T): string} getter
 * @return {ValidatorFn}
 */
export function notInValidator<T>(
  list?: T[],
  getter?: (el: T) => string
): ValidatorFn {
  getter = getter || ((el) => el as unknown as string);
  return (control: AbstractControl): ValidationErrors | null => {
    if (!list) {
      return null;
    }
    const valueExists = list.find(
      (el: T) => getter!(el).trim() === control.value.trim()
    );
    return valueExists
      ? errorObjectBuilder('inserted-value-already-present')
      : null;
  };
}

/**
 * Checks if serial is valid through some rules
 * @param {string} serialNumber - The number of the equipment
 * @return {ValidatorFn}
 */
export function serialValidator(serialNumber?: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const serial = (control.value as string) || serialNumber;
    if (serial?.length > 11) {
      return null;
    }
    const reg = new RegExp(
      '^[a-zA-Z0-9]{5}[XYZxyz0-9]{1}[0-9]{4}[a-zA-Z0-9]?$'
    );
    const valid = reg.test(serial);
    return valid ? null : errorObjectBuilder('invalid-serial-error-message');
  };
}

/**
 * Ensures at least one of the given controls (by name) inside a FormGroup is checked/truthy
 * @param {string[]} controlNames names of the controls to check within the group
 * @return {ValidatorFn}
 */
export function atLeastOneCheckedValidator(
  controlNames: string[]
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const anyChecked = controlNames.some((name) => !!group.get(name)?.value);
    return anyChecked ? null : errorObjectBuilder('select-at-least-one-filter');
  };
}

/**
 * Checks email pattern
 * @return {ValidatorFn}
 */
export function emailPattern(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const input: string = control.value;
    const error = ['invalid-email'];
    if (!input) return null;
    const regExp = new RegExp(
      '^' +
        /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+/.source +
        /(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@/.source +
        /(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/
          .source +
        '$'
    );
    const isValidEmail = Boolean(input?.match(regExp));

    return isValidEmail ? null : error;
  };
}
